import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST, IsDSM, IsEmployee } from 'config/config';
import MerchandisingProgramSeasonCategoryEnum, {
  MerchandisingProgramSeasonCategoryEnumValue,
} from 'enums/MerchandisingProgramSeasonCategoryEnum';
import { useSelector } from 'react-redux';
import useToast from 'services/Hooks/useToast';
import tableDataQueryStringBuilder from 'services/Utilities/TableData';

const normalizeDealerIds = dealerIds => dealerIds?.filter(Boolean) ?? [];

export const usePrefetchMerchandisingProgramQuote = () => {
  const queryClient = useQueryClient();
  queryClient.prefetchQuery({
    queryKey: ['merchandisingProgramQuote'],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/merchandising-program-quotes/active');
      return response.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export const useMerchandisingProgramQuotes = (tableDataParam, dealerIds = []) => {
  const { errorMessage } = useToast();
  const impersonationDealerId = useSelector(state => state.AccountReducer?.profile?.temporaryDealer_Id);
  const normalizedDealerIds = normalizeDealerIds(dealerIds);
  const requestParams = normalizedDealerIds.length ? { ...tableDataParam, dealerIds: normalizedDealerIds } : tableDataParam;

  const query = useQuery({
    queryKey: [
      'merchandisingProgramQuotes',
      JSON.stringify(tableDataParam),
      IsDSM || IsEmployee ? impersonationDealerId : 'default',
      normalizedDealerIds.join('|'),
    ],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/merchandising-program-quotes', {
        paramsSerializer: params => tableDataQueryStringBuilder(params),
        params: requestParams,
      });
      return response.data;
    },
    throwOnError: errorMessage,
    staleTime: 0,
  });
  return query;
};

export const useActiveMerchandisingProgramQuote = isLogged => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['merchandisingProgramQuote'],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/merchandising-program-quotes/active');
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!isLogged,
  });
  return query;
};

export const useMerchandisingProgramQuote = merchandisingProgramQuoteId => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['historyMerchandisingProgramQuote', merchandisingProgramQuoteId],
    queryFn: async () => {
      if (merchandisingProgramQuoteId) {
        const response = await axios.get(
          `${HOST}/api/dealer-portal/merchandising-program-quotes/${merchandisingProgramQuoteId}`,
        );
        return response.data;
      } else {
        return null;
      }
    },
    throwOnError: errorMessage,
  });
  return query;
};

export const usePostMerchandisingProgramQuoteItem = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addMerchandisingProgramQuoteItem'],
    mutationFn: async merchandisingProgramQuoteItem => {
      return await axios.post(
        `${HOST}/api/dealer-portal/merchandising-program-quotes/items`,
        isDemo ? { ...merchandisingProgramQuoteItem, isDemo } : merchandisingProgramQuoteItem,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['merchandisingProgramQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostMerchandisingProgramQuoteCheckout = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addMerchandisingProgramQuoteItem'],
    mutationFn: async merchandisingProgramQuote => {
      return await axios.post(
        `${HOST}/api/dealer-portal/merchandising-program-quotes/${merchandisingProgramQuote.id}/checkout`,
      );
    },
    onSuccess: () => {
      saveMessage();
      queryClient.setQueryData(['merchandisingProgramQuote'], null);
      queryClient.setQueryData(['brpClubMerchandisingProgramQuote'], null);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostChangeMerchandisingProgramQuoteItemQuantity = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeQuantityMerchandisingProgramQuoteItem'],
    mutationFn: async merchandisingProgramQuoteItem => {
      return await axios.post(
        `${HOST}/api/dealer-portal/merchandising-program-quotes/items/${merchandisingProgramQuoteItem.id}/quantity`,
        merchandisingProgramQuoteItem,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['merchandisingProgramQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchMerchandisingProgramQuoteChangeShipping = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['merchandisingProgramQuoteChangeShipping'],
    mutationFn: async merchandisingProgramQuote => {
      return await axios.patch(
        `${HOST}/api/dealer-portal/merchandising-program-quotes/${merchandisingProgramQuote.id}/shipping`,
        merchandisingProgramQuote,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['merchandisingProgramQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchMerchandisingProgramQuoteChangeInformation = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['merchandisingProgramQuoteChangeInformation'],
    mutationFn: async merchandisingProgramQuote => {
      return await axios.patch(
        `${HOST}/api/dealer-portal/merchandising-program-quotes/${merchandisingProgramQuote.id}/information`,
        merchandisingProgramQuote,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['merchandisingProgramQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchApproveMerchandisingProgramQuote = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['approveMerchandisingProgramQuote'],
    mutationFn: async merchandisingProgramQuote => {
      return await axios.patch(
        `${HOST}/api/dealer-portal/merchandising-program-quotes/${merchandisingProgramQuote.id}/status`,
        merchandisingProgramQuote,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['merchandisingProgramQuotes'] });
      queryClient.setQueryData(['historyMerchandisingProgramQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteMerchandisingProgramQuoteItem = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteMerchandisingProgramQuoteItem'],
    mutationFn: async merchandisingProgramQuoteItem => {
      return await axios.delete(
        `${HOST}/api/dealer-portal/merchandising-program-quotes/items/${merchandisingProgramQuoteItem.id}`,
      );
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['merchandisingProgramQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteMerchandisingProgramQuote = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteMerchandisingProgramQuote'],
    mutationFn: async merchandisingProgramQuote => {
      return await axios.delete(`${HOST}/api/dealer-portal/merchandising-program-quotes/${merchandisingProgramQuote.id}`);
    },
    onSuccess: () => {
      deleteMessage();
      queryClient.setQueryData(['merchandisingProgramQuote'], null);
    },
    onError: errorMessage,
  });
  return query;
};

export const useMerchandisingProgramQuoteSortedSeason = () => {
  const query = useQuery({
    queryKey: ['merchandisingProgramQuoteSortedSeason'],
    queryFn: async () => {
      return MerchandisingProgramSeasonCategoryEnum.filter(
        e =>
          e.value !== MerchandisingProgramSeasonCategoryEnumValue.Summer &&
          e.value !== MerchandisingProgramSeasonCategoryEnumValue.Winter,
      ).sort((a, b) => a.order - b.order);
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return query;
};

// Section BRP CLUB
export const usePostBrpClubMerchandisingProgramQuoteItem = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addBrpClubMerchandisingProgramQuoteItem'],
    mutationFn: async merchandisingProgramQuoteItem => {
      return await axios.post(
        `${HOST}/api/dealer-portal/brp-club/merchandising-program-quotes/items`,
        isDemo ? { ...merchandisingProgramQuoteItem, isDemo } : merchandisingProgramQuoteItem,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['merchandisingProgramQuote'], data);
      queryClient.setQueryData(['brpClubMerchandisingProgramQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};
